<template>
  <div v-if="scoreToBeShown.length > 0">
    <div class="title mb-3">{{ $t('curriculum.title') }}</div>
    <b-container class="mt-4"
                 v-show="availablePhases.length > 1 || availableSessions.length > 1">
      <b-row class="header text-center">
        <b-col>{{ $t('filters.phase') }}</b-col>
        <b-col>{{ $t('filters.session') }}</b-col>
      </b-row>
      <b-row class="text-center">
        <b-col>
          <combo-or-label v-model="phase" :options="availablePhases"></combo-or-label>
        </b-col>
        <b-col>
          <combo-or-label v-model="session" :options="availableSessions"></combo-or-label>
        </b-col>
      </b-row>
    </b-container>
    <b-container>
      <template v-for="round in scoreToBeShown">
        <b-row class="mt-3 currHeader mb-3">
          <b-col>
            R.{{ round.round }}
          </b-col>
          <b-col cols="10"
                 v-html="round.opponent">
          </b-col>
        </b-row>
        <template v-for="board in round.boards">
          <b-form-row class="mb-2 curriculumRow">
            <b-col cols="1">
              {{ board.BoardNumber }}
            </b-col>
            <b-col cols="4" class="boardResult">
              <b-spinner v-if="board.Alter === -1" small variant="success"></b-spinner>
              <template v-else>
                <k-progress v-if="board.PercNs != null"
                            :percent="Math.round(isOurNsSide(board) ? board.PercNs : board.PercEw)"
                            :color="val => formatColor(val/100)"></k-progress>
                <div v-else v-text="isOurNsSide(board) ? board.PuntiNs : board.PuntiEw"
                     :class="['font-weight-bold', computeScoreColor(board)]"></div>
              </template>
            </b-col>
            <b-col cols="3">
              <div v-html="addColorToSuite(board.TextualContract)"></div>
            </b-col>
            <b-col cols="2">
              <div v-html="addColorToSuite(board.TextualLead)"></div>
            </b-col>
            <b-col cols="2" class="text-right">
              {{ board.TextualScore }}
            </b-col>
          </b-form-row>
        </template>
      </template>
    </b-container>
  </div>
</template>
<script>
import {mapGetters} from 'vuex';
import chroma from 'chroma-js';
import ComboOrLabel from "../components/lib/ComboOrLabel";
import {addColorToSuite as _addColorToSuite} from "../lib/cardSuits";
import individualContest from "../mixins/individualContest";

export default {
  name: 'Curriculum',
  components: {ComboOrLabel},
  mixins: [individualContest],
  data() {
    return {
      formatColor: chroma.scale(['red', 'yellow', 'green']),
      phase: 1,
      session: 1
    };
  },
  computed: {
    ...mapGetters({
      playerContestHistoryMap: 'players/playerContestHistoryMap',
      globalDeals: 'deals/played',
      pairsList: 'players/pairsList',
      played: 'deals/played'
    }),
    availablePhases() {
      return [...new Set(Object.values(this.playerContestHistoryMap).map(s => s.phase).sort((a, b) => a - b))];
    },
    availableSessions() {
      return [...new Set(this.playerContestHistoryMap.filter(ris => this.phase === ris.phase).map(ris => ris.session).sort((a, b) => a - b))];
    },
    ourScores() {
      const ranksets = this.globalDeals
          ?.Phases?.[this.phase]
          ?.Sessions?.[this.session]
          ?.Ranksets ?? {};

      return Object.values(ranksets)
          .flatMap(rankset => Object.values(rankset?.Boards ?? {}))
          .flatMap(board => board?.Scores ?? [])
          .filter(score => this.isOurScore(score));
    },
    scoreToBeShown() {
      const blocks = this.playerContestHistoryMap
          .filter(ris => this.phase === ris.phase)
          .filter(ris => this.session === ris.session)
          .map(ris => ({
            round: ris.round,
            opponent: this.getOppositeName(ris),
            boards: this.getScores(ris)
          }));

      const shown = new Set(blocks.flatMap(block => block.boards).map(board => board.BoardNumber));

      this.ourScores
          .filter(score => !shown.has(score.BoardNumber))
          .forEach(score => {
            shown.add(score.BoardNumber);
            const existing = blocks.find(block => block.round === score.Round);
            if (existing) {
              existing.boards.push(score);
              return;
            }
            blocks.push({
              round: score.Round,
              opponent: this.getOppositeNameFromScore(score),
              boards: [score]
            });
          });

      blocks.forEach(block => block.boards.sort((a, b) => a.BoardNumber - b.BoardNumber));

      return blocks.sort((a, b) => a.round - b.round);
    }
  },
  methods: {
    computeScoreColor(result) {
      const points = this.isOurNsSide(result) ? result.PuntiNs : result.PuntiEw;
      const intPoints = parseInt(points);
      return intPoints > 0 ? "greenResult" : intPoints < 0 ? "redResult" : "yellowResult";
    },
    addColorToSuite(contract) {
      return _addColorToSuite(contract);
    },
    getOppositeName(score) {
      if (this.isIndividual) {
        const playerLabel = code => this.playersByCode[code]?.Name ?? code;
        const ns = [playerLabel(score.N), playerLabel(score.S)].join(" - ");
        const ew = [playerLabel(score.E), playerLabel(score.W)].join(" - ");
        return [0, 2].includes(score.position) ? ew : ns;
      }

      return score.NsEventId === this.pairId
          ? this.pairsList[score.EwEventId]?.Name ?? ""
          : this.pairsList[score.NsEventId]?.Name ?? "";
    },
    getOppositeNameFromScore(score) {
      if (this.isIndividual) {
        const playerLabel = id => this.playersList[id]?.Name ?? id;
        return this.isOurNsSide(score)
            ? [playerLabel(score.Ieast), playerLabel(score.Iwest)].join(" - ")
            : [playerLabel(score.Inorth), playerLabel(score.Isouth)].join(" - ");
      }

      const opponentId = this.isOurNsSide(score) ? score.EWex : score.NSex;
      return this.pairsList[opponentId]?.Name ?? "";
    },
    getScores(score) {
      const that = this;
      return score.PlayedBoards.flatMap(brdNumber => that.globalDeals
          ?.Phases[score.phase]
          ?.Sessions[score.session]
          ?.Ranksets[score.rankset]
          ?.Boards[brdNumber]
          ?.Scores
          ?.filter(score => that.isOurScore(score)) ?? null
      ).filter(el => el !== null)
    }
  }
}
;
</script>

<style scoped>
.currHeader {
  background-color: #C7DDE5;
}

>>> .k-progress {
  margin: 0 !important;
}

.curriculumRow {
  line-height: 20px;
}


.title {
  width: 100%;
  margin: 0 auto;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e3a5f;
  letter-spacing: -0.01em;
}
</style>
